import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { faLock } from "@fortawesome/pro-regular-svg-icons";

import { CampaignDisplayStatus } from "@signalist/shared-types";

import { Icon } from "@/components/Icon/Icon";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import { classNames } from "@/utils/classNames";

import { CAMPAIGN_STATUS_CONFIG } from "./campaignStatus.config";

/**
 * Status pill for the campaign-builder header (PREC-3249): a colored icon + label with a
 * dark tooltip shown on hover and focus. Exact tooltip text lives in i18n.
 */
export const CampaignStatusBadge: FunctionComponent<{ status: CampaignDisplayStatus }> = ({
	status,
}) => {
	const { t } = useTranslation();
	const config = CAMPAIGN_STATUS_CONFIG[status];

	return (
		<Tooltip
			content={t(`campaign.builder.status.tooltips.${status}`)}
			direction="bottom"
			variant="dark"
		>
			<div className="flex items-center gap-1 cursor-default" tabIndex={0}>
				<Icon className={classNames("w-4 h-4", config.iconClass)} icon={config.icon} />
				<span className="text-text-meta text-sm whitespace-nowrap">
					{t(`campaign.builder.status.labels.${status}`)}
				</span>
			</div>
		</Tooltip>
	);
};

/**
 * Lock indicator next to a locked campaign's name (PREC-3249). With `onClick` it becomes the
 * builder's trigger for the full lock explainer (PREC-3786); without one it stays the plain
 * hover tooltip used in the dense copywriting table, where there is no room for a modal.
 */
export const CampaignLockIcon: FunctionComponent<{ onClick?: () => void }> = ({ onClick }) => {
	const { t } = useTranslation();
	const icon = <Icon className="w-4 h-4 text-status-info" icon={faLock} />;

	return (
		<Tooltip content={t("campaign.builder.status.lock")} direction="bottom" variant="dark">
			{onClick ? (
				<button
					aria-label={t("campaignLockExplainer.bannerTitle")}
					className="flex items-center cursor-pointer"
					type="button"
					onClick={onClick}
				>
					{icon}
				</button>
			) : (
				<div className="flex items-center cursor-default" tabIndex={0}>
					{icon}
				</div>
			)}
		</Tooltip>
	);
};
